import type { ElementType } from 'react';
import { useRef, useState } from 'react';
import type {
    LoaderFunctionArgs,
    MetaFunction,
    SerializeFrom,
} from '@remix-run/node';
import { json } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { useLoaderData, useLocation } from '@remix-run/react';
import { useBoolean, useSize } from 'ahooks';
import invariant from 'tiny-invariant';
import { getPreloadImageLink } from '@modules/images';
import {
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createProductSchema } from '@modules/seo/schema/product-schema';
import { createLDSchema } from '@modules/seo/util/create-ld-schema';
import createProductOGTags from '@modules/product/util/create-product-og-tags';
import type { loader as rootLoader } from '~/root';
import Variants from '@modules/product/component/variants/Variants';
import ProductDetails from '@modules/product/component/product-details/ProductDetails';
import {
    VariantSelectionProvider,
    useVariantSelectionState,
} from '@modules/product/context/VariantSelectionContext';
import PageHeader from '@modules/product/component/page-header/PageHeader';
import ProductContainer from '@modules/product/component/product-container/ProductContainer';
import AboutSeller from '@modules/product/component/about-seller/AboutSeller';
import type {
    GetFeaturedProjectsQuery,
    GetFeaturedProjectsQueryVariables,
    GetProductMainQuery,
    GetProductMainQueryVariables,
    GetProductShippingQuery,
    GetProductShippingQueryVariables,
    GetProductVariantsProfessionalPricingQuery,
    GetProductVariantsProfessionalPricingQueryVariables,
    GetRegionsQuery,
    GetRegionsQueryVariables,
    GetShoppingCartSummaryQuery,
    GetShoppingCartSummaryQueryVariables,
} from 'generated/graphql';
import {
    GetFeaturedProjectsDocument,
    GetProductMainDocument,
    GetProductShippingDocument,
    GetProductVariantsProfessionalPricingDocument,
    GetRegionsDocument,
    GetShoppingCartSummaryDocument,
    PermissionEnum,
} from 'generated/graphql';
import {
    Flex,
    Grid,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
    Breadcrumb,
} from '@archipro-design/aria';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import ProductGalleryMobile from '@modules/product/component/photo-gallery/ProductGallery.mobile';
import ProductGalleryDesktop from '~/modules/product/component/photo-gallery/ProductGallery.desktop';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import {
    assertResponse,
    response404,
} from '~/modules/root/graphql/assertResponse.server';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { extractBranchFromNetwork } from '@modules/enquiry/util/extract-branch-from-network.server';
import { getPermissions, useUser } from '@modules/user';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import SampleEnquiryPopup from '@modules/product/component/sample-enquiry-popup/SampleEnquiryPopup';
import type { APHandle } from '@modules/root';
import { useHydrated } from 'remix-utils';
import { hasFeature } from '@modules/root';
import { getPrimaryCTA } from '@modules/professional/util/professional-cta-helper';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { processProduct } from '~/modules/product/util/process-product.server';
import { processCart } from '~/modules/product/util/process-cart.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import TradeOnlyBanner from '~/modules/product/component/trade-only-banner/TradeOnlyBanner';
import type { loader as appLoader } from '~/routes/_app';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { User } from '~/server/types';
import { getSession } from '~/modules/root/util/session.server';
import StickyAction from '@modules/directory/component/StickyAction';
import EnquirePurchaseStickyAction from '@modules/product/component/variant-purchase/EnquirePurchaseStickyAction';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { getProfessionalLocations } from '~/modules/product/util/get-professional-locations';
import { useEnquiryModalByUrl } from '~/modules/enquiry/hook/use-enquiry-modal-by-url';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { getDirectoryItemByIDs } from '~/modules/directory/api/get-directory-items-admin.server';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import { Link } from '@remix-run/react';
import { ProductIsland } from '~/modules/product/component/product-island/ProductIsland';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const fallbackProductTitle =
            data?.product?.Professional?.Title &&
            data?.product?.Title &&
            `${data.product.Professional.Title} ${data.product.Title}`;

        const title = createMetaTitle(props, [
            data?.localisation?.DirectoryHeader?.Title,
            fallbackProductTitle,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.product?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.product?.LibraryImages?.[0]?.LibraryImageFile
                ?.DetailPageSquare?.Desktop
        );

        const productSchema = createProductSchema(props);
        const ldSchema = createLDSchema([productSchema]);
        // ToDo: Video schema

        const preloadImages = prefetchProductImages(props);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.product?.FacebookPreviewImage ?? fallbackSocialImage,
            }),
            ...createProductOGTags(
                data?.product,
                data?.shoppingCartSummary?.Currency
            ),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.product?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.product?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation
            ),
        ];
    }
);

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/modules/free-mode.min.css',
    },
];

const prefetchProductImages: MetaFunction<
    typeof loader,
    { root: typeof rootLoader; 'routes/_app': typeof appLoader }
> = (props) => {
    const { data, matches } = props;
    const { appData } = getRouteData(matches);
    const imageConfigProps = appData?.imageConfigProps;
    if (!data?.product) {
        return [];
    }

    const defaultImage =
        data.product.DefaultImage?.LibraryImageFile?.DetailPageSquare;
    const { Desktop, Mobile } = defaultImage ?? {};

    if (!data?.product) return [];

    if (!Desktop) {
        return [];
    }

    if (Mobile && data.deviceType === 'mobile') {
        return [
            ...getPreloadImageLink(
                imageConfigProps,
                Mobile,
                undefined,
                getImagesSizes({ desktop: 780, mobile: 375 })
            ),
        ];
    }

    return [
        ...getPreloadImageLink(
            imageConfigProps,
            Desktop,
            undefined,
            getImagesSizes(780)
        ),
    ];
};

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
};

export const shouldRevalidate: ShouldRevalidateFunction = ({ formAction }) => {
    return !formAction?.includes('add-to-shopping-cart');
};

const DESKTOP_SECTION_GAP = 'space-240';
const MOBILE_SECTION_GAP = 'space-80';

export const loader = async (args: LoaderFunctionArgs) => {
    const { request, params, context } = args;
    invariant(params.urlSegment, 'A product URL segment is required');

    const searchParams = new URL(request.url).searchParams;
    const productPromise = context.client.query<
        GetProductMainQuery,
        GetProductMainQueryVariables
    >({
        query: GetProductMainDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productVariantsProfessionalPricingPromise = context.client.query<
        GetProductVariantsProfessionalPricingQuery,
        GetProductVariantsProfessionalPricingQueryVariables
    >({
        query: GetProductVariantsProfessionalPricingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productShippingPromise = context.client.query<
        GetProductShippingQuery,
        GetProductShippingQueryVariables
    >({
        query: GetProductShippingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });
    const cartSummaryPromise = context.client.query<
        GetShoppingCartSummaryQuery,
        GetShoppingCartSummaryQueryVariables
    >({
        query: GetShoppingCartSummaryDocument,
    });

    const regionPromise = context.client.query<
        GetRegionsQuery,
        GetRegionsQueryVariables
    >({
        query: GetRegionsDocument,
        variables: {
            country: context.localisation.country,
            public: true,
        },
    });

    const getFeaturedProjectsLimit = (
        searchParams: URLSearchParams
    ): number => {
        return Number(searchParams.get('featuredProjectsShowAll') || 4);
    };

    const featuredProjectsPromise = context.client.query<
        GetFeaturedProjectsQuery,
        GetFeaturedProjectsQueryVariables
    >({
        query: GetFeaturedProjectsDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
            limit: getFeaturedProjectsLimit(new URL(request.url).searchParams),
        },
    });

    const localisationPromise = getPageLocalisation(args);

    const [
        user,
        productResult,
        productVariantsProfessionalPricingResult,
        productShippingResult,
        cartSummaryResult,
        regionResult,
        featuredProjectsResult,
        localisation,
    ] = await Promise.all([
        context.getUser(),
        productPromise,
        productVariantsProfessionalPricingPromise,
        productShippingPromise,
        cartSummaryPromise,
        regionPromise,
        featuredProjectsPromise,
        localisationPromise,
    ]);
    const ecommerceEnabled = hasFeature(user.FeatureFlags, 'ecommerce');
    const stickyPurchaseActionEnabled = hasFeature(
        user.FeatureFlags,
        'sticky_mobile_cta'
    );

    unsafeVariant_DO_NOT_USE(productResult);
    unsafeVariant_DO_NOT_USE(productVariantsProfessionalPricingResult);
    unsafeVariant_DO_NOT_USE(productShippingResult);
    unsafeVariant_DO_NOT_USE(cartSummaryResult);
    unsafeVariant_DO_NOT_USE(regionResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);

    const product = await assertResponse(
        args.context,
        productResult.data.Directory.Product,
        productResult.error
    );

    const productShipping = await assertResponse(
        args.context,
        productShippingResult.data.Directory.Product,
        productShippingResult.error
    );

    const productVariantsProfessionalPricing = await assertResponse(
        args.context,
        productVariantsProfessionalPricingResult.data.Directory.Product,
        productVariantsProfessionalPricingResult.error
    );

    const cartSummary = await assertResponse(
        args.context,
        cartSummaryResult.data.ShoppingCart,
        cartSummaryResult.error
    );
    invariant(cartSummary, 'Failed to load cart');

    const regions = regionResult.data.Regions;
    invariant(regions, 'Failed to load regions');

    const featuredProjects = await assertResponse(
        args.context,
        featuredProjectsResult.data.Directory.Product,
        featuredProjectsResult.error
    );

    if (!productVariantsProfessionalPricing.Professional) {
        throw await response404(args.context);
    }

    const networks = productVariantsProfessionalPricing.Professional.Networks;
    const branchOptions = extractBranchFromNetwork(networks);

    const processedProduct = processProduct(
        product,
        productVariantsProfessionalPricing,
        productShipping,
        user,
        args.context.appDisplayMode
    );

    const processedCart = processCart(
        cartSummary,
        processedProduct.Professional
    );

    const productEditPath = getBMPagePath(
        processedProduct.Professional.URLSegment,
        'products',
        processedProduct.URLSegment
    );

    const detailButton = {
        professionalId: processedProduct.Professional.ID,
        editLink: productEditPath,
        shareableLink: product.Link,
        id: product.ID,
        canEdit: getPermissions(
            user,
            PermissionEnum.BmProductsEdit,
            processedProduct.Professional.ID
        ),
        canCopyLink: getPermissions(
            user,
            PermissionEnum.BmMarketingView,
            processedProduct.Professional.ID
        ),
    };

    const processedFeaturedProjects = {
        FeaturedProjects: {
            pageInfo: featuredProjects.FeaturedProjects.pageInfo,
            edges: featuredProjects.FeaturedProjects.edges.map((edge) => {
                return {
                    node: mapProjectToTileViewModel(edge.node, {
                        appDisplayMode: context.appDisplayMode,
                    }),
                };
            }),
        },
    };

    const showSampleEnquiry =
        searchParams.get('sample') === '2' && product.SamplesAvailable;

    const primaryCTA = getPrimaryCTA({
        primaryCTA: processedProduct.Professional.PrimaryCTA,
        pageType: 'product',
        isManufacturer: processedProduct.Professional.IsManufacturer,
    });

    // Permissions granted
    const isGranted = {
        editAdvertAdmin: getPermissions(user, PermissionEnum.BmAdvertAdminEdit),
        supportView: getPermissions(user, PermissionEnum.BmSupportView),
    };

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        processedProduct.Professional.Country,
        context.localisation.country,
        !processedProduct.IsLive,
        'product',
        processedProduct?.Professional.Disable,
        {
            showPageNotLiveWarning: isGranted.supportView,
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        }
    );

    const { locations: showrooms, linkedProfiles } = getProfessionalLocations(
        product.Professional,
        ['Showroom']
    );

    const whereToBuyEnabled =
        hasFeature(user.FeatureFlags, 'show_where_to_buy_products') &&
        showrooms.length > 0;

    const session = await getSession(request);

    let tilesAdminData: DirectoryItemAdmin[] = [];
    if (isGranted.editAdvertAdmin) {
        const ids = processedProduct.ProductRange.products.map(
            (product) => product.id
        );
        const alsoFromProductsIds =
            processedProduct.AlsoFromProducts.products.map(
                (product) => product.id
            );
        const installersIds = processedProduct.Installers.professionals.map(
            (professional) => professional.id
        );
        const allIds = [...ids, ...alsoFromProductsIds, ...installersIds];
        tilesAdminData = await getDirectoryItemByIDs({
            ...args.context,
            IDs: allIds,
        });
    }

    return json(
        {
            deviceType: context.device.type,
            isGranted,
            product: processedProduct,
            shoppingCartSummary: processedCart,
            regions: regions,
            featuredProjects: processedFeaturedProjects,
            branchOptions,
            detailButton,
            localisation,
            showSampleEnquiry,
            ecommerceEnabled,
            stickyPurchaseActionEnabled,
            primaryCTA,
            warningMessages,
            whereToBuyEnabled,
            showrooms,
            linkedProfiles,
            tilesAdminData,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

export type ProductPageLoaderData = SerializeFrom<typeof loader>;

const WrappedTradeOnlyBanner = ({ user }: { user: User }) => {
    const { selectedVariant } = useVariantSelectionState();
    return <TradeOnlyBanner user={user} selectedVariant={selectedVariant} />;
};

const ProductPage = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
        detailButton,
        showSampleEnquiry,
        ecommerceEnabled,
        stickyPurchaseActionEnabled,
        primaryCTA,
        whereToBuyEnabled,
        tilesAdminData,
    } = useLoaderData<typeof loader>();

    const { css } = useStyles();
    const { mobile, desktop } = useAppDisplayModeContext();
    const user = useUser();
    const hydrated = useHydrated();

    const [enquiryModalOpen, { setFalse: closeEnquiryModal }] =
        useBoolean(showSampleEnquiry);

    const productEditPath = getBMPagePath(
        product.Professional?.URLSegment,
        'products',
        product.URLSegment
    );
    useSetEditMode(productEditPath, product.Professional?.ID);

    const defaultImage =
        product.DefaultImage?.LibraryImageFile?.DetailPageSquare;

    const headerRef = useRef<HTMLDivElement>(null);
    const headerSize = useSize(headerRef);
    const sentinelRef = useRef<HTMLDivElement>(null);
    //sections to hide the sticky action.
    const primaryCTASectionRef = useRef<HTMLDivElement>(null);
    const enquireSectionRef = useRef<HTMLDivElement>(null);
    const extrasSectionRef = useRef<HTMLDivElement>(null);

    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);

    const { renderEnquiryModal, openEnquiryModal } = useEnquiryModalByUrl();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const showAllFeaturedProjects =
        searchParams.get('featuredProjectsShowAll') === '0';

    return (
        <ProductContainer>
            <div
                ref={sentinelRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                }}
            />
            <VariantSelectionProvider product={product}>
                {!mobile && (
                    <PageHeader
                        ref={headerRef}
                        data={{ ...product, PrimaryCTA: primaryCTA }}
                        isSticky={false}
                        openEnquiryModal={openEnquiryModal}
                    />
                )}
                <Flex
                    column
                    gap={mobile ? MOBILE_SECTION_GAP : DESKTOP_SECTION_GAP}
                >
                    <VariantOptionProvider product={product}>
                        <Grid
                            columns={mobile ? '1fr' : `${pxToRem(880)} 1fr`}
                            className={css({
                                columnGap: pxToRem(44),
                                paddingTop: mobile ? undefined : pxToRem(40),
                            })}
                            design={{
                                display: mobile ? 'block' : 'grid',
                            }}
                        >
                            {mobile ? (
                                <ProductGalleryMobile
                                    data={product}
                                    firstSlide={defaultImage?.Mobile ?? ''}
                                    title={product.Title}
                                />
                            ) : (
                                <ProductGalleryDesktop
                                    data={product}
                                    firstSlide={{
                                        src: defaultImage?.Desktop ?? '',
                                        isMultiplied:
                                            product?.DefaultImage
                                                ?.IsMultiplied ?? false,
                                    }}
                                    title={product.Title}
                                    loop={true}
                                    headerHeight={headerSize?.height}
                                />
                            )}
                            {mobile && (
                                <PageHeader
                                    ref={headerRef}
                                    data={{
                                        ...product,
                                        PrimaryCTA: primaryCTA,
                                    }}
                                    openEnquiryModal={openEnquiryModal}
                                />
                            )}
                            <div>
                                <div className="hidden justify-end md:flex md:-mt-[2rem] md:pb-2">
                                    <Breadcrumb
                                        logo={null}
                                        home={{
                                            link: true,
                                            as: Link,
                                            label: 'Home',
                                            to: '/',
                                            variables: {
                                                breadcrumbItemTextColor:
                                                    '#b6b6b6',
                                            },
                                        }}
                                        items={product.Breadcrumbs.map(
                                            (breadcrumb) => ({
                                                label: breadcrumb.label,
                                                href: breadcrumb.href,
                                                as: breadcrumb.as as ElementType,
                                                variables: {
                                                    breadcrumbItemTextColor:
                                                        '#b6b6b6',
                                                },
                                            })
                                        )}
                                        data-testid="breadcrumb-menu"
                                        variables={{
                                            breadcrumbFontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                                <div className="border-t border-black">
                                    {!mobile && ecommerceEnabled && (
                                        <>
                                            <SpecialtyPricingBanner
                                                product={product}
                                            />
                                            <WrappedTradeOnlyBanner
                                                user={user}
                                            />
                                        </>
                                    )}
                                    <Variants
                                        data={{
                                            product: product,
                                            ecommerceEnabled,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                    <ProductDetails
                                        data={{
                                            ...product,
                                            primaryCTA,
                                            primaryCTASectionRef,
                                            enquireSectionRef,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                </div>
                            </div>
                        </Grid>
                    </VariantOptionProvider>

                    <div
                        ref={extrasSectionRef}
                        className={
                            'flex flex-col px-4 md:px-0 gap-y-8 md:gap-y-[8rem]'
                        }
                    >
                        {/* Product Range */}
                        <DetailsPageRelatedTileGrid
                            type="products"
                            title={`Other products in this range`}
                            tiles={
                                product.ProductRange.products.map((product) => {
                                    return {
                                        model: product,
                                        aspectRatio: 'square',
                                        section: 'ProductRange',
                                    };
                                }) ?? []
                            }
                            cta={
                                product.ProductRange.pageInfo.totalCount >
                                (mobile ? 4 : 8)
                                    ? {
                                          type: 'link',
                                          to: getProductRangeUrl(
                                              product.Professional,
                                              product.LibraryRange?.Title ?? ''
                                          ),
                                          label: 'View more',
                                      }
                                    : undefined
                            }
                            displayCount={{
                                desktop: 10,
                                mobile: 4,
                            }}
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                            tilesAdminData={tilesAdminData}
                        />

                        {/* Projects featuring */}
                        <DetailsPageRelatedTileGrid
                            type="projects"
                            title={`Projects featuring ${product.Title}`}
                            tiles={
                                FeaturedProjects.edges.map(({ node }) => {
                                    return {
                                        model: node,
                                        aspectRatio: 'default',
                                        section: 'ProductFeaturedProjects',
                                    };
                                }) ?? []
                            }
                            cta={
                                !showAllFeaturedProjects &&
                                FeaturedProjects.pageInfo.totalCount >
                                    (mobile ? 4 : 8)
                                    ? {
                                          type: 'link',
                                          to: '?featuredProjectsShowAll=0',
                                          label: 'View all',
                                          preventScrollReset: true,
                                      }
                                    : undefined
                            }
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                            displayCount={{
                                desktop: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 8,
                                mobile: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 4,
                            }}
                        />

                        {/* Also From Products, only show if there are no products in the range */}
                        {product.ProductRange.pageInfo.totalCount === 0 && (
                            <DetailsPageRelatedTileGrid
                                type="products"
                                title={`More from ${product.Professional?.Title}`}
                                tiles={
                                    product.AlsoFromProducts.products.map(
                                        (product) => {
                                            return {
                                                model: product,
                                                aspectRatio: 'square',
                                                section: 'ProductRelated',
                                            };
                                        }
                                    ) ?? []
                                }
                                cta={
                                    product.AlsoFromProducts.pageInfo
                                        .totalCount > (mobile ? 4 : 10)
                                        ? {
                                              type: 'link',
                                              to: `${product.Professional.Link}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                              label: 'View more',
                                          }
                                        : undefined
                                }
                                appDisplayMode={mobile ? 'mobile' : 'desktop'}
                                displayCount={{
                                    desktop: 10,
                                    mobile: 4,
                                }}
                                tilesAdminData={tilesAdminData}
                            />
                        )}

                        {/* Installers */}
                        <DetailsPageRelatedTileGrid
                            type="professionals"
                            title={`Installers of ${product.Title}`}
                            tiles={
                                product.Installers.professionals.map(
                                    (professional) => {
                                        return {
                                            model: professional,
                                            aspectRatio: 'default',
                                            section: 'ProductInstallers',
                                        };
                                    }
                                ) ?? []
                            }
                            cta={
                                product.Installers.pageInfo.totalCount >
                                (mobile ? 2 : 4)
                                    ? {
                                          type: 'showAll',
                                      }
                                    : undefined
                            }
                            displayCount={{
                                desktop: 4,
                                mobile: 2,
                            }}
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                            tilesAdminData={tilesAdminData}
                        />
                    </div>
                    {!mobile && <AboutSeller data={product} />}
                </Flex>
                <DetailPageButtons {...detailButton} />
                {showSampleEnquiry && hydrated && !!product.Professional && (
                    <SampleEnquiryPopup
                        data={{ product }}
                        open={enquiryModalOpen}
                        onClose={closeEnquiryModal}
                    />
                )}

                {stickyPurchaseActionEnabled && mobile && (
                    <StickyAction
                        revealBounds={[1, 3000]}
                        position={{ bottom: 0 }}
                        height={74}
                        hiddenOn={[
                            { ref: primaryCTASectionRef, threshold: 0 },
                            { ref: enquireSectionRef, threshold: 0.1 },
                            { ref: extrasSectionRef, threshold: 0 },
                        ]}
                    >
                        <EnquirePurchaseStickyAction
                            product={product}
                            primaryCTA={primaryCTA}
                        />
                    </StickyAction>
                )}
                {whereToBuyEnabled && (
                    <ProductLocationsDrawer
                        product={product}
                        open={whereToBuyOpen}
                        toggleModal={setWhereToBuyOpen}
                    />
                )}

                {renderEnquiryModal({
                    professional: {
                        ...product.Professional,
                        primaryCTA,
                        ArchiproEditorialAccount: false,
                        Website: product.Professional?.Website ?? '',
                    },
                    siteTreeID: product.ID,
                })}

                {desktop && (
                    <ProductIsland
                        product={product}
                        openEnquiryModal={openEnquiryModal}
                        primaryCTA={primaryCTA}
                    />
                )}
            </VariantSelectionProvider>
        </ProductContainer>
    );
};

export default ProductPage;

const getProductRangeUrl = (
    professional: { Link: string; URLSegment: string },
    rangeName: string
) => {
    const professionalProducts = `${professional.Link}/our-products`;

    // Note: Filtering is currently done using url search params which may change
    // to path params in the future.
    const searchParams = new URLSearchParams();
    if (rangeName) {
        searchParams.set('productRanges', rangeName);
    }
    if (professional.URLSegment) {
        searchParams.set('professionals', professional.URLSegment);
    }
    const constructRangeParams = searchParams.toString();

    return constructRangeParams
        ? `${professionalProducts}?${constructRangeParams}#${PROFESSIONAL_SECTIONS_ANCHOR}`
        : professionalProducts;
};
